import {
  useDeleteTicketMutation,
  useGetTicketsQuery,
  usePermanentDeleteTicketMutation,
  useRestoreTicketMutation,
  useUpdateTicketMutation,
} from "../../api/ticketApi/ticket";
import { Badge, Button, Card, Popover, Spinner } from "flowbite-react";
import TicketTable from "../../components/Table";
import { QueryParams, Ticket } from "@/sharedTypes";
import { useNavigate } from "react-router";
import TicketFormDialog from "../TicketFormDialog"; // Ensure TicketFormDialog is imported
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import TicketFormFilter from "../../components/TicketFormFilter";
import { HiOutlineEye, HiPencilAlt, HiTrash } from "react-icons/hi";
import { FaFilter } from "react-icons/fa";
import { MdOutlineSettingsBackupRestore } from "react-icons/md";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import UseErrorHandler from "@/components/UseErrorHandler";
import { toast } from "react-toastify";
import { getErrorMessage } from "@/utils/requestUtils";

export default function TicketManagement() {
  const [queryParams, setQueryParams] = useState<QueryParams>({
    filters: { is_active: true },
    pagination: {
      pageSize: 15,
      page: 1,
    },
  });

  const {
    data,
    isError,
    error,
    isLoading: isFetching,
  } = useGetTicketsQuery(queryParams);

  UseErrorHandler({
    title: "Failed to fetch tickets",
    isError,
    error,
  });

  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [ticketToUpdate, setTicketToUpdate] = useState<number | undefined>(
    undefined,
  );

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [confirmationDialog, setConfirmationDialog] = useState<{
    title: string;
    body: string;
    onCancel: () => void;
    onConfirm: () => void;
    isLoading: boolean;
  } | null>(null);

  const navigate = useNavigate();
  // Add react hook form methods
  const methods = useForm();
  const [showInactive, setShowInactive] = useState(false);

  const handleEdit = (ticket: Ticket) => {
    const ticktFormFields = {
      ticket_title: ticket.ticket_title,
      ticket_description: ticket.ticket_description,
      last_priority_id: ticket.last_priority_id,
      last_category_id: ticket.last_category_id || null,
      last_subcategory_id: ticket.last_subcategory_id || null,
      last_assigned_agent_id: ticket.last_assigned_agent_id || null,
      update_reason_id: ticket.update_reason_id || null,
      last_status_id: ticket.last_status_id || null,
      client_id: ticket.client_id || null,
    };

    setTicketToUpdate(ticket?.ticket_id);

    methods.reset(ticktFormFields);
    setIsFormDialogOpen(true);
  };

  const [updateTicket, { isLoading }] = useUpdateTicketMutation();

  const handleUpdate = async (data: Ticket) => {
    const clientId = typeof data.client_id === "number"
    ? data.client_id
    : Number(String(data.client_id).trim());

    if (!Number.isFinite(clientId) || clientId <= 0) {
      toast.error("client_id geçersiz");
      return;
    }

    console.log("Updating ticket with data:", { ...data, client_id: clientId, ticketId: ticketToUpdate });
    
    updateTicket({ ...data, client_id: clientId, ticketId: ticketToUpdate as number })
      .unwrap()
      .then(() => {
        toast.success("Ticket updated successfully");
        methods.reset();
        setIsFormDialogOpen(false);
      })
      .catch((error: any) => {
        toast.error(`${getErrorMessage(error)}`);
      });
  };

  const handleFormDialogClose = () => {
    setIsFormDialogOpen(false);
    setTicketToUpdate(undefined);
  };

  //Filtering logic
  const filterMethods = useForm<any>({
    defaultValues: {
      ticket_title: "",
      ticket_description: "",
      last_priority_id: "",
      last_category_id: "",
      last_subcategory_id: null,
      client_id: "",
      last_assigned_agent_id: "",
      is_active: showInactive ? false : true,
    },
  });

  const handleFilterApply = (appliedFilters: Ticket) => {
    setQueryParams((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        ...appliedFilters,
        is_active: !showInactive,
      },
      pagination: {
        ...prev.pagination,
        page: 1, // Reset to the first page
      },
    }));
    setIsFilterOpen(false);
  };

  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        is_active: !showInactive,
      },
    }));
  }, [showInactive]);

  //Permanant deletion
  const [permanentDeleteTicket, { isLoading: deletingPermanently }] =
    usePermanentDeleteTicketMutation();

  const handlePermanentDelete = (ticketId: string) => {
    setConfirmationDialog({
      title: "Confirm Permanent Deletion",
      body: "Are you sure you want to permanently delete this ticket? This action cannot be undone.",
      onCancel: () => setConfirmationDialog(null),
      onConfirm: () =>
        permanentDeleteTicket(ticketId)
          .unwrap()
          .then(() => {
            toast.success("Ticket permanently deleted");
            setConfirmationDialog(null);
          })
          .catch((error: any) => {
            toast.error(`${getErrorMessage(error)}`);
            setConfirmationDialog(null);
          }),
      isLoading: deletingPermanently,
    });
  };

  //Restoration
  const [restoreTicket, { isLoading: isRestoring }] =
    useRestoreTicketMutation();

  const handleRestore = (ticketId: string) => {
    setConfirmationDialog({
      title: "Confirm Restoration",
      body: "Are you sure you want to restore this ticket?",
      onCancel: () => setConfirmationDialog(null),
      onConfirm: () =>
        restoreTicket(ticketId)
          .unwrap()
          .then(() => {
            toast.success("Ticket restored successfully");
            setConfirmationDialog(null);
          })
          .catch((error: any) => {
            toast.error(`${getErrorMessage(error)}`);
            setConfirmationDialog(null);
          }),
      isLoading: isRestoring,
    });
  };

  //Soft deletion
  const [deleteTicket, { isLoading: isDeleting }] = useDeleteTicketMutation();

  const handleDelete = (ticketId: string) => {
    setConfirmationDialog({
      title: "Confirm Deletion",
      body: "Are you sure you want to delete this ticket?",
      onCancel: () => setConfirmationDialog(null),
      onConfirm: () =>
        deleteTicket(ticketId)
          .unwrap()
          .then(() => {
            toast.success("Ticket deleted successfully");
            setConfirmationDialog(null);
          })
          .catch((error: any) => {
            toast.error(`${getErrorMessage(error)}`);
            setConfirmationDialog(null);
          }),
      isLoading: isDeleting,
    });
  };

  const columns = [
    { key: "ticket_id", label: "Ticket ID" },
    { key: "ticket_title", label: "Title" },
    { key: "status", label: "Status" },
    { key: "priority", label: "Priority" },
    { key: "category", label: "Category" },
    { key: "subcategory", label: "Subcategory" },
    { key: "createdAt", label: "Created At" },
    { key: "assigned_agent", label: "Assigned agent" },
    { key: "channel", label: "Channel" , render: (row: any) => {
       const channel = Number(row.channel);
       if(channel === 1) {
          return <Badge className="bg-green-600 text-white px-2 py-1 rounded-md">Email</Badge>
        } else if (channel === 0) {
          return <Badge className="bg-blue-600 text-white px-2 py-1 rounded-md">Platform</Badge>
        } else if (channel === 2) {
          return <Badge className="bg-purple-600 text-white px-2 py-1 rounded-md">AI-Widget</Badge>
        } else {
          return <Badge className="bg-gray-600 text-white px-2 py-1 rounded-md">Other</Badge>
      }
    }},
 
    {
      key: "actions",
      label: "Actions",
      render: (row: any) => (
        <div className="flex space-x-2">
          {showInactive ? (
            <>
              <Button
                size="xs"
                color="success"
                onClick={() => handleRestore(row.ticket_id)}
                title="Restore"
              >
                <MdOutlineSettingsBackupRestore className="h-4 w-4" />
              </Button>
              <Button
                size="xs"
                color="failure"
                onClick={() => handlePermanentDelete(row.ticket_id)}
                title="Delete Permanently"
              >
                <HiTrash className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                size="xs"
                onClick={() => navigate(`/tickets/${row.ticket_id}`)}
                title="View"
              >
                <HiOutlineEye className="h-4 w-4" />
              </Button>
              <Button
                size="xs"
                onClick={() => handleEdit(row)}
                color="purple"
                title="Edit"
              >
                <HiPencilAlt className="h-4 w-4" />
              </Button>
              <Button
                size="xs"
                color="failure"
                onClick={() => handleDelete(row.ticket_id)}
                title="Delete"
              >
                <HiTrash className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  const formattedData =
    data?.items.map((ticket: any) => ({
      ...ticket,
      ticket_id: ticket.ticket_id, // Include ticket_id for the View action
      ticket_title: ticket.ticket_title,
      ticket_description: ticket.ticket_description,
      status: ticket.status.status_name,
      priority: ticket.priority.priority_name,
      createdAt: new Date(ticket.createdAt).toLocaleString(),
      category: ticket.category?.category_name || "N/A",
      subcategory: ticket.subcategory?.subcategory_name || "N/A",
      assigned_agent:
        ticket.last_assigned_agent?.agent_username || "Unassigned",
      channel: ticket.channel || "",
      })) || [];

      console.log("Formatted Data:", formattedData);
  const handlePaginationChange = (newPageIndex: number) => {
    setQueryParams((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        page: newPageIndex,
      },
    }));
  };

  return (
    <>
      <Card className="overflow-x-auto">
        <p className="text-base font-normal text-gray-500 dark:text-gray-400">
          Manage your tickets with ICont along with 24/7 support.
        </p>

        <div className="space-y-4 sm:flex sm:space-x-3 sm:space-y-0"></div>
        <div>
          {isFetching ? (
            <div className="flex w-full items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <p className="text-red-500">Failed to load tickets.</p>
          ) : (
            <div>
              <Popover
                content={
                  <div className="w-96 p-5">
                    {" "}
                    {/* Increased width from w-80 to w-96 */}
                    <FormProvider {...filterMethods}>
                      <TicketFormFilter
                        filter={filterMethods.getValues()}
                        onFilterApply={handleFilterApply}
                        onCancel={() => setIsFilterOpen(false)}
                        showInactive={showInactive}
                        setShowInactive={setShowInactive}
                      />
                    </FormProvider>
                  </div>
                }
              >
                <Button
                  color="primary"
                  className="mb-4 inline-flex w-full items-center justify-center rounded-lg text-center text-sm font-medium text-white sm:w-auto"
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                  <FaFilter
                    className="-ml-1 mr-2 h-4 w-4 text-current"
                    color="white"
                  />
                  Filter
                </Button>
              </Popover>
              <TicketTable
                columns={columns}
                data={formattedData}
                pagination={data?.pagination}
                onPageChange={handlePaginationChange}
              />
            </div>
          )}
        </div>
      </Card>
      <FormProvider {...methods}>
        <TicketFormDialog
          open={isFormDialogOpen}
          onClose={handleFormDialogClose}
          mode="edit"
          onSubmit={handleUpdate}
          isLoading={isLoading}
        />
      </FormProvider>
      {confirmationDialog && (
        <ConfirmationDialog
          title={confirmationDialog.title}
          body={confirmationDialog.body}
          onCancel={confirmationDialog.onCancel}
          onConfirm={confirmationDialog.onConfirm}
          isLoading={confirmationDialog.isLoading}
        />
      )}
    </>
  );
}
